import React, { useState, useEffect, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
} from '@tanstack/react-table';
import { createColumns, InventoryItem } from './tableColumns';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Product } from './ProductFilter';

interface InventoryTableProps {
  onStoreChange?: (store: string) => void;
  onProductsDetected?: (productCount: number) => void;
  searchResults?: Product[];
  isSearchActive?: boolean;
  onRefreshInventory?: (refreshFn: () => void) => void;
}

let data: InventoryItem[] = [];
import { LIST_INVENTORY, DELETE_PRODUCT } from '~/services/store';

const InventoryTable: React.FC<InventoryTableProps> = ({
  onStoreChange,
  onProductsDetected,
  searchResults,
  isSearchActive,
  onRefreshInventory,
}) => {
  const [t] = useTranslation('store');
  const { agentId } = useParams();
  const navigate = useNavigate();

  const [listInventory, { loading, error }] = useLazyQuery(LIST_INVENTORY, {
    variables: { data: { agent_id: agentId } },
    fetchPolicy: 'network-only', // Esto forzará una recarga desde el servidor
    onCompleted: (data) => {
      if (data?.list_inventory?.data.Inventory) {
        setTableData(data.list_inventory.data.Inventory);
      }
      if (data?.list_inventory?.data.Store) {
        setStoreType(data.list_inventory.data.Store);
        if (onStoreChange) {
          onStoreChange(data.list_inventory.data.Store);
        }
      }

      if (onProductsDetected) {
        onProductsDetected(data?.list_inventory?.data?.Inventory?.length || 0);
      }
    },
  });

  const [deleteProduct] = useMutation(DELETE_PRODUCT, {
    onCompleted: (data) => {
      if (!data.delete_product.hasError) {
        showToastMessage('Producto eliminado exitosamente', false);
        // Reload the inventory after successful deletion
        listInventory();
      } else {
        showToastMessage(
          data.delete_product.message || 'Error al eliminar el producto',
          true,
        );
      }
      setShowDeleteDialog(false);
    },
    onError: (error) => {
      console.error('Error deleting product:', error);
      showToastMessage('Error al eliminar el producto', true);
      setShowDeleteDialog(false);
    },
  });

  const showToastMessage = (mensaje: string, isWarning: boolean) => {
    if (isWarning) setShowToastAlert(true);
    else setShowToastSuccess(true);
    setMessage(mensaje);
    setTimeout(() => {
      if (isWarning) setShowToastAlert(false);
      else setShowToastSuccess(false);
    }, 3000);
  };

  const handleDeleteProduct = (productId: string, productTitle: string) => {
    setProductToDelete({ id: productId, title: productTitle });
    setShowDeleteDialog(true);
  };

  const confirmDeleteProduct = () => {
    if (!agentId || !productToDelete) {
      showToastMessage('ID del agente o producto no disponible', true);
      return;
    }

    deleteProduct({
      variables: {
        data: {
          agent_id: agentId,
          product_id: productToDelete.id,
        },
      },
    });
  };

  const tableRef = useRef<HTMLDivElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const [tableData, setTableData] = useState<InventoryItem[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [storeType, setStoreType] = useState<string>('');

  // States for delete functionality
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [showToastAlert, setShowToastAlert] = useState(false);
  const [showToastSuccess, setShowToastSuccess] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (agentId) {
      listInventory();
    }
  }, [agentId, listInventory]);

  // Expose refresh function to parent component
  useEffect(() => {
    if (onRefreshInventory) {
      onRefreshInventory(listInventory);
    }
  }, [onRefreshInventory, listInventory]);

  // Convert search results to InventoryItem format
  const convertSearchResultsToInventoryItems = (
    products: Product[],
  ): InventoryItem[] => {
    return products.map((product) => ({
      _id: product._id,
      title: product.title,
      description: product.description,
      mainImageURL: product.mainImageURL || null,
      priceRangeV2: product.priceRangeV2,
      totalInventory: product.totalInventory,
      variants: product.variants,
      onlineStorePreviewUrl: product.onlineStorePreviewUrl || null,
      updatedAt: new Date().toISOString(), // Use current date for search results
      score: product.score, // Add vector search score for potential display
    }));
  };

  const filteredData = React.useMemo(() => {
    // If we have vector search results, use them
    if (isSearchActive && searchResults) {
      return convertSearchResultsToInventoryItems(searchResults);
    }

    // Otherwise, return all table data (no traditional text filtering)
    return tableData;
  }, [tableData, isSearchActive, searchResults]);

  const table = useReactTable({
    data: filteredData,
    columns: createColumns(navigate, t, storeType, handleDeleteProduct),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
      sorting: [
        {
          id: 'title',
          desc: false,
        },
      ],
    },
  });

  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.type === 'show-description-dialog') {
        setDialogData(customEvent.detail);
        setDialogOpen(true);
      }
    };
    const current = tableRef.current;
    if (current) {
      current.addEventListener(
        'show-description-dialog',
        handler as EventListener,
      );
    }
    return () => {
      if (current) {
        current.removeEventListener(
          'show-description-dialog',
          handler as EventListener,
        );
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full space-y-4">
        <div className="card bg-base-100 border-base-300 border shadow-xl">
          <div className="card-body">
            <div className="flex items-center justify-center py-12">
              <span className="loading loading-spinner loading-lg"></span>
              <span className="text-base-content ml-4">{t('loading')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div
        ref={tableRef}
        className="card bg-base-100 border-base-300 border shadow-xl"
      >
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr className="border-base-300">
                  {table.getFlatHeaders().map((header) => (
                    <th
                      key={header.id}
                      className="bg-base-200 text-base-content"
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={`flex items-center gap-2 ${
                            header.column.getCanSort()
                              ? 'hover:text-base-content/80 cursor-pointer select-none'
                              : ''
                          }`}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.getCanSort() && (
                            <span className="flex flex-col">
                              {header.column.getIsSorted() === 'asc' ? (
                                <ChevronUpIcon className="text-primary h-4 w-4" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ChevronDownIcon className="text-primary h-4 w-4" />
                              ) : (
                                <div className="flex flex-col">
                                  <ChevronUpIcon className="h-3 w-3 opacity-50" />
                                  <ChevronDownIcon className="-mt-1 h-3 w-3 opacity-50" />
                                </div>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-base-200 border-base-300 bg-base-100"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="text-base-content">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between gap-2">
        <div className="join">
          <button
            className="join-item btn btn-sm"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            «
          </button>
          <button
            className="join-item btn btn-sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            ‹
          </button>
          <button
            className="join-item btn btn-sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            ›
          </button>
          <button
            className="join-item btn btn-sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            »
          </button>
        </div>

        <div className="text-sm">
          <span className="font-medium">
            {t('pagination.page')} {table.getState().pagination.pageIndex + 1}{' '}
            {t('pagination.of')} {table.getPageCount()}
          </span>
        </div>
      </div>

      {/* Description Dialog Modal */}
      <div className={`modal ${dialogOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <button
            className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
            onClick={() => setDialogOpen(false)}
          >
            ✕
          </button>
          <h3 className="mb-4 text-lg font-bold">{dialogData?.title}</h3>
          <p className="whitespace-pre-line py-4">{dialogData?.description}</p>
        </div>
        <div
          className="modal-backdrop"
          onClick={() => setDialogOpen(false)}
        ></div>
      </div>

      {/* Toast notifications */}
      {showToastAlert && (
        <div className="toast toast-center">
          <div className="alert alert-warning">
            <span>{message}</span>
          </div>
        </div>
      )}

      {showToastSuccess && (
        <div className="toast toast-center">
          <div className="alert alert-success">
            <span>{message}</span>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <div className={`modal ${showDeleteDialog ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <div className="flex items-start gap-4">
            <div className="avatar placeholder">
              <div className="bg-error/20 text-error w-12 rounded-full">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  ></path>
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold">Confirmar eliminación</h3>
              <p className="text-base-content/70 py-4">
                ¿Estás seguro de que quieres eliminar el producto "
                {productToDelete?.title}"? Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
          <div className="modal-action">
            <button
              className="btn btn-ghost"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancelar
            </button>
            <button className="btn btn-error" onClick={confirmDeleteProduct}>
              Eliminar
            </button>
          </div>
        </div>
        <div
          className="modal-backdrop"
          onClick={() => setShowDeleteDialog(false)}
        ></div>
      </div>
    </div>
  );
};

export default InventoryTable;
